/*
 * Local override of a Horizon core file. This file is otherwise kept at upstream parity —
 * re-check this change on every `git pull upstream main`.
 *
 * Changed: two things, which together take the network off the perceived path when a shopper
 * changes variant.
 *
 *   1. Every variant's gallery image is preloaded once the page is idle, so no image request
 *      is needed at the moment of the change.
 *   2. Choosing a variant points the gallery's visible image at that image immediately.
 *      Horizon's section fetch still runs, in parallel, and stays the source of truth for
 *      price, availability, the URL and the rest of the gallery.
 *
 * Why: upstream does no client-side swap at all. `fetchUpdatedSection` goes to the server and
 * `assets/media-gallery.js` replaces the whole gallery with the response, so the image could
 * not even begin downloading until that HTML arrived — and with `hide_variants` on, an
 * unselected variant's image is absent from the page, making it a cold fetch stacked behind
 * the round-trip. Preloading alone could not fix this: the visible image does not change
 * until `replaceWith` runs, so the round-trip stayed in the wait no matter how warm the cache
 * was. Hence the swap.
 *
 * Hover, focus and touch still warm a single image, covering a swatch chosen before the idle
 * preload has run and raising priority once a tap is underway.
 *
 * Paired with `snippets/variant-main-picker.liquid`, which emits `data-warm-media-url` and
 * `data-warm-media-ratio`. Note that is the product page's picker; `variant-swatches.liquid`
 * serves product cards and is untouched.
 */
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * Gallery image URLs already requested, so repeated hovers over the same swatch cost
 * nothing. Keyed by the full URL, width included, so a resize warms afresh.
 *
 * @type {Set<string>}
 */
const warmedMediaUrls = new Set();

/**
 * The events that mean "this swatch is about to be chosen". `pointerover` rather than
 * `pointerenter` because only the former bubbles, so one delegated listener covers every
 * swatch. `focusin` covers keyboard, and `pointerdown`/`touchstart` cover touch, where
 * there is no hover before the tap — on those two the click is imminent, so the warm is
 * raised to high priority.
 */
const WARM_EVENTS = ['pointerover', 'focusin', 'pointerdown', 'touchstart'];
const IMMINENT_EVENTS = ['pointerdown', 'touchstart'];

/**
 * Finds the image the media gallery is currently displaying, to read the width the browser
 * resolved for that slot. Deliberately does not match inside the zoom dialog, whose media
 * are rendered at `sizes="100vw"` and would give a misleading width.
 *
 * @returns {HTMLImageElement | null}
 */
const findGalleryImage = () => {
  const selectors = [
    'media-gallery slideshow-slide[aria-hidden="false"] img.product-media__image',
    'media-gallery .media-gallery__grid img.product-media__image',
  ];

  for (const selector of selectors) {
    const image = document.querySelector(selector);
    if (image instanceof HTMLImageElement) return image;
  }

  return null;
};

/**
 * Rewrites a Shopify CDN image URL to a given rendered width.
 *
 * @param {string} masterUrl - The variant image URL, carrying a `width` param.
 * @param {string} width - The width to request.
 * @returns {string}
 */
const urlAtWidth = (masterUrl, width) => {
  const url = new URL(masterUrl, location.href);
  url.searchParams.set('width', width);

  return url.href;
};

/**
 * The width the gallery has actually resolved for its slot.
 *
 * Read from the live image rather than recomputed, because the gallery's `sizes` derives
 * from media-gallery block settings this component cannot see. `currentSrc` is the candidate
 * the browser chose, so a preload at the same width is the one the gallery will ask for.
 *
 * Returns null rather than guessing while no candidate has been chosen: falling back to the
 * 3840px master would pull a large image nobody needs and miss the cache the swap relies on.
 *
 * @returns {string | null}
 */
const resolvedGalleryWidth = () => {
  const galleryImage = findGalleryImage();
  if (!galleryImage?.currentSrc) return null;

  return new URL(galleryImage.currentSrc, location.href).searchParams.get('width');
};

/**
 * Fetches a variant's gallery image ahead of time, so a colour change paints from cache
 * rather than issuing a request.
 *
 * @param {string | undefined} masterUrl - The variant image URL, carrying a `width` param.
 * @param {boolean} imminent - Whether a click is about to follow, which raises priority.
 */
const warmGalleryImage = (masterUrl, imminent) => {
  if (!masterUrl) return;

  const width = resolvedGalleryWidth();
  if (!width) return;

  const href = urlAtWidth(masterUrl, width);
  if (warmedMediaUrls.has(href)) return;
  warmedMediaUrls.add(href);

  const image = new Image();
  image.fetchPriority = imminent ? 'high' : 'low';
  image.decoding = 'async';
  image.src = href;
};

/**
 * Points the gallery's visible image at the chosen variant's image straight away, so the
 * change is seen at once instead of after the section fetch. Horizon's fetch still runs, in
 * parallel, and replaces the gallery with the server's markup — landing on the same image,
 * so the swap is not visible a second time.
 *
 * `srcset` and `sizes` are cleared so the browser uses exactly the URL that was preloaded,
 * which makes the cache hit certain rather than likely. Both are transient; the server's
 * markup restores a full srcset moments later.
 *
 * @param {string | undefined} masterUrl - The variant image URL, carrying a `width` param.
 * @param {string | undefined} ratio - The image's aspect ratio, to hold the slot's geometry.
 */
const swapGalleryImage = (masterUrl, ratio) => {
  if (!masterUrl) return;

  const width = resolvedGalleryWidth();
  const galleryImage = findGalleryImage();
  if (!width || !galleryImage) return;

  const href = urlAtWidth(masterUrl, width);
  if (galleryImage.currentSrc === href) return;

  // Hold the slot's geometry, so a differently proportioned image does not shift the layout
  // in the moment before the server's markup arrives carrying the correct ratio.
  if (ratio) {
    galleryImage.closest('.product-media')?.style.setProperty('--ratio', ratio);
    galleryImage.closest('slideshow-slide')?.style.setProperty('--media-preview-ratio', ratio);
  }

  galleryImage.srcset = '';
  galleryImage.sizes = '';
  galleryImage.src = href;
};

// Short-lived, single-use server responses: never guess prices or inventory.
const variantSectionPreloads = new Map();
const VARIANT_SECTION_PRELOAD_TTL = 15000;

/** @param {string} url @param {AbortSignal} [signal] */
async function preloadVariantSection(url, signal) {
  const existing = variantSectionPreloads.get(url);
  if (existing && Date.now() - existing.createdAt < VARIANT_SECTION_PRELOAD_TTL) return;
  if (variantSectionPreloads.size >= 4) {
    variantSectionPreloads.delete(variantSectionPreloads.keys().next().value);
  }
  const entry = { createdAt: Date.now(), text: null };
  variantSectionPreloads.set(url, entry);
  try {
    const response = await fetch(url, { signal, priority: 'low' });
    if (!response.ok) throw new Error('Variant preload failed');
    const text = await response.text();
    if (variantSectionPreloads.get(url) === entry) entry.text = text;
  } catch {
    if (variantSectionPreloads.get(url) === entry) variantSectionPreloads.delete(url);
  }
}

/** @param {string} url @returns {string | null} */
function consumeVariantSection(url) {
  const entry = variantSectionPreloads.get(url);
  if (!entry) return null;
  if (Date.now() - entry.createdAt >= VARIANT_SECTION_PRELOAD_TTL) {
    variantSectionPreloads.delete(url);
    return null;
  }
  if (!entry.text) return null;
  variantSectionPreloads.delete(url);
  return entry.text;
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #warmController = new AbortController();

  /** Whether this picker drives a media gallery, i.e. is on the product page. */
  #warmEnabled = false;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Only the product page has a media gallery waiting on the section fetch. A picker in a
    // card or a quick-add dialog does its own local image swap, so warming there would just
    // fetch a page-sized image nobody is about to see.
    this.#warmEnabled = !this.closest('product-card, quick-add-dialog');

    if (this.#warmEnabled) {
      const { signal } = this.#warmController;

      // Hover, focus and touch remain a fast path: they cover a swatch chosen before the
      // idle preload has run, and raise priority when a tap is already underway.
      for (const type of WARM_EVENTS) {
        this.addEventListener(type, this.#warmVariantMedia, { signal, passive: true });
      }

      this.#schedulePreloadAll(signal);
    }
  }

  /**
   * Queues the preload of every variant image for once the page is idle. Deliberately not
   * run during connect: these images are not needed for first paint and must not compete
   * with it.
   *
   * @param {AbortSignal} signal - Aborted when the component disconnects.
   */
  #schedulePreloadAll = (signal) => {
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(this.#preloadAllVariantMedia, { timeout: 3000 });
      } else {
        setTimeout(this.#preloadAllVariantMedia, 1000);
      }
    };

    // `load` guarantees the gallery image has resolved a candidate, which is what gives the
    // preload its width. Before that, `resolvedGalleryWidth` would decline to guess.
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal });
  };

  /**
   * Preloads the gallery image of every unselected variant, so a colour change needs no
   * request at all. Low priority throughout — none of it is on the critical path.
   */
  #preloadAllVariantMedia = () => {
    for (const input of this.querySelectorAll('input[data-warm-media-url]')) {
      if (!(input instanceof HTMLInputElement) || input.checked) continue;

      warmGalleryImage(input.dataset.warmMediaUrl, false);
    }
    this.#preloadSimpleVariantSections();
  };

  // Limit speculative requests to simple, same-product pickers after first paint.
  #preloadSimpleVariantSections = () => {
    if (
      this.dataset.templateProductMatch !== 'true' ||
      !this.dataset.sectionId ||
      this.closest('product-card, quick-add-dialog, featured-product-information') ||
      this.selectedOptionsValues.length !== 1 ||
      this.querySelectorAll('fieldset, select').length !== 1
    ) return;

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    for (const input of Array.from(this.querySelectorAll('input[type="radio"], select option')).slice(0, 4)) {
      if (input.checked || input.selected) continue;
      const connectedUrl = input.dataset.connectedProductUrl;
      if (connectedUrl && connectedUrl !== currentUrl) continue;
      const pendingUrl = this.#pendingRequestUrl;
      const url = this.buildRequestUrl(input, 'product-card', [input.dataset.optionValueId]);
      this.#pendingRequestUrl = pendingUrl;
      void preloadVariantSection(url, this.#warmController.signal);
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#warmController.abort();
  }

  /**
   * Warms the gallery image of the swatch the shopper is about to choose.
   * @param {Event} event - A pointer, touch or focus event from one of WARM_EVENTS.
   */
  #warmVariantMedia = (event) => {
    if (!(event.target instanceof Element)) return;

    const input = event.target.closest('input[data-warm-media-url]');
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    warmGalleryImage(input.dataset.warmMediaUrl, IMMINENT_EVENTS.includes(event.type));
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // The browser checks a radio before the server response arrives. Reflect the
    // intended colour immediately and never display the previous variant's stock.
    const fieldset = selectedOption.closest('fieldset');
    const selectedLabel = fieldset?.querySelector('.variant-option__swatch-value');
    if (selectedLabel) selectedLabel.textContent = selectedOption.value;
    const productSection = this.closest('.shopify-section, featured-product-information, dialog');
    if (this.#warmEnabled && productSection) {
      const inventoryText = productSection.querySelector('.product-inventory__text');
      if (inventoryText) inventoryText.textContent = 'Checking availability…';
      const inventoryIcon = productSection.querySelector('.product-inventory__icon');
      if (inventoryIcon) inventoryIcon.style.visibility = 'hidden';
      productSection.querySelectorAll('button[name="add"], sticky-add-to-cart button').forEach((button) => {
        button.disabled = true;
      });
      productSection.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]').forEach((checkout) => {
        checkout.setAttribute('hidden', 'true');
      });
    }

    // Paint the chosen variant's image at once, from the cache the preload filled. The fetch
    // below still runs, in parallel, and remains the source of truth for price, availability,
    // the URL and the rest of the gallery.
    if (this.#warmEnabled) {
      swapGalleryImage(selectedOption.dataset.warmMediaUrl, selectedOption.dataset.warmMediaRatio);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-dialog': this.dataset.sectionId,
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // Same-product selections need only the product section. Fetch the full page
    // for combined-listing navigation, where other main sections can also change.
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const requestedProductUrl = productUrl?.split('?')[0];
    if (
      this.dataset.templateProductMatch === 'true' &&
      this.dataset.sectionId &&
      !this.closest('product-card') &&
      requestedProductUrl === currentProductUrl
    ) {
      return `${requestedProductUrl}?section_id=${this.dataset.sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const signal = this.#abortController.signal;
    const requestController = this.#abortController;
    const requestDeadline = setTimeout(() => {
      requestController.abort(new DOMException('Variant update timed out', 'TimeoutError'));
    }, 10000);
    const preloadedSection = consumeVariantSection(requestUrl);
    const responseTextPromise = preloadedSection !== null
      ? Promise.resolve(preloadedSection)
      : fetch(requestUrl, { signal }).then((response) => response.text());

    responseTextPromise
      .then((responseText) => {
        signal.throwIfAborted();
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          if (requestController === this.#abortController && this.#warmEnabled) {
            const inventoryText = this.closest('.shopify-section, featured-product-information, dialog')
              ?.querySelector('.product-inventory__text');
            if (inventoryText) inventoryText.textContent = 'Unable to confirm stock. Please refresh this page.';
          }
          console.error(error);
        }
      })
      .finally(() => clearTimeout(requestDeadline));
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
